// Functions
import {createDeleteFunction} from './shared';

// Classes
import {DisplayField} from '../DisplayField';
import {Link} from '@/lib/classes/Link';

/**
 * @typedef {import('@/lib/classes/InfoWindowContent/aggregators/shared.js').Aggregator} Aggregator
 */

/**
 * @type {Aggregator}
 */
const address = {
  /**
   * Given parcel blob data, formats it as an address display field
   * This is read-only and deletable
   * @param {Object} blob the incoming parcel data
   * @return {[DisplayField, Function]} [addressDisplayField, deleteFunc]
   * addressDisplayField is a display field object for the parent display fields array
   * deleteFunc is a function that removes keys used here from the original blob
   */
  function: function(blob) {
  // An array of keys we want to use from the blob
    const streetAddressBlobKeys = ['addr_number', 'addr_street_name', 'addr_street_type'];

    // Make the street address by filtering, mapping, and joining the keys
    const streetAddress = streetAddressBlobKeys
      .filter((key) => blob[key])
      .map((key) => blob[key])
      .join(' ');

    // Create Street Address, City, State, and Zip display fields
    const displayFields = [
      new DisplayField({
        label: 'Address',
        unit: 'string',
        value: streetAddress || undefined,
        allowEdit: true,
        allowLabelEdit: true,
        allowDelete: true,
      }),
      new DisplayField({
        label: 'City',
        unit: 'string',
        value: blob['muni_name'] || undefined,
        allowEdit: true,
        allowLabelEdit: true,
        allowDelete: true,
      }),
      new DisplayField({
        label: 'State',
        unit: 'string',
        value: blob['state_abbr'] || undefined,
        allowEdit: true,
        allowLabelEdit: true,
        allowDelete: true,
      }),
      new DisplayField({
        label: 'Zip Code',
        unit: 'string',
        value: blob['census_zip'] || undefined,
        allowEdit: true,
        allowLabelEdit: true,
        allowDelete: true,
      }),
    ];

    // The address display field
    const addressDisplayField = new DisplayField({
      label: 'Address',
      unit: 'address',
      displayFields,
      allowEdit: true,
      allowLabelEdit: true,
      allowDelete: true,
    });

    // An array of the other fields that were used to make the address
    const otherAddressBlobKeys = ['muni_name', 'state_abbr', 'census_zip'];

    // Create a function for deleting parcel address keys from the original blob using the keysAndLabels array keys
    const deleteFunc = createDeleteFunction(streetAddressBlobKeys.concat(otherAddressBlobKeys));

    // Return the address display field and the delete function
    return [addressDisplayField, deleteFunc];
  },
  name: 'address',
};


/**
 * @type {Aggregator}
 */
const mailingAddress = {
  /**
   * Given parcel blob data, formats it as a mailing address display field
   * This is read-only and deletable
   * @param {Object} blob the incoming parcel data
   * @return {Array<DisplayField, Function>} [addressDisplayField, deleteFunc]
   * addressDisplayField is a display field object for the parent display fields array
   * deleteFunc is a function that removes keys used here from the original blob
   */
  function: function(blob) {
    let mailAddress = null;
    const firstAddressExists = blob['mail_address1'] && blob['mail_address1'] !== '';
    const thirdAddressExists = blob['mail_address3'] && blob['mail_address3'] !== '';
    // If the addresses exist, combine them into one string
    if (firstAddressExists && thirdAddressExists) {
      mailAddress = blob['mail_address1'].concat(', ', blob['mail_address3']);
    }

    // a display field for mailing address
    const displayField = new DisplayField({
      label: 'Mailing Address',
      unit: 'string',
      value: mailAddress,
      allowEdit: true,
      allowLabelEdit: true,
      allowDelete: true,
    });

    // Create a function to delete keys from the original blob with the inputted key values
    const deleteFunc = createDeleteFunction(['mail_address1', 'mail_address3']);

    return [displayField, deleteFunc];
  },
  name: 'mailingAddress',
};

const legalDescription = {
  function: function(blob) {
    const descriptionKeys = ['legal_desc1', 'legal_desc2', 'legal_desc3'];

    let description = '';
    let hasDescription = false;
    descriptionKeys.forEach((key) => {
      if (blob[key]) {
        hasDescription = true;
        description += blob[key];
      }
    });

    // If none of the keys were found in the blob, set the description to null
    if (!hasDescription) {
      description = null;
    }

    const displayField = new DisplayField({
      label: 'Legal Description',
      unit: 'string',
      value: description,
      allowEdit: true,
      allowLabelEdit: true,
      allowDelete: true,
    });

    const deleteFunc = createDeleteFunction(descriptionKeys);

    return [displayField, deleteFunc];
  },
  name: 'legalDescription',
};

const websiteLink = {
  function: function(blob) {
    let url = null;
    if (blob.county_id && blob.parcel_id) {
      url = `https://reportallusa.com/cama_redir?county_id=${blob.county_id}&parcel_id=${blob.parcel_id}`;
    }

    // a display field for website link
    const displayField = new DisplayField({
      label: `Assessor's Website`,
      unit: 'link',
      value: new Link({
        url,
        text: 'Click Here',
        type: 'link',
      }),
      allowEdit: true,
      allowLabelEdit: true,
      allowDelete: true,
    });

    // Create a function to delete keys from the original blob with the inputted key values
    const deleteFunc = createDeleteFunction(['county_id', 'parcel_id']);

    return [displayField, deleteFunc];
  },
  name: 'websiteLink',
};

export const aggregators = [
  address,
  mailingAddress,
  legalDescription,
  websiteLink,
];
